import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, inr, timeAgo, fmtTime, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PackageCalculator from "@/components/PackageCalculator";
import PricingEditor from "@/components/PricingEditor";
import { Card } from "@/components/ui/card";
import { PaymentStages } from "@/components/PaymentStages";
import { AddressCard } from "@/components/AddressCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, FileCheck, Plane, ShieldCheck, Receipt, Upload, Download, Trash2,
  Calculator as CalcIcon, StickyNote, Wallet, Plus, PhoneCall, Settings2, Copy,
} from "lucide-react";

const DOC_TYPES = [
  { type: "Umrah E-Visa (KSA)", icon: FileCheck },
  { type: "Flight PNR & Ticket", icon: Plane },
  { type: "NOC / Ministry Clearance", icon: ShieldCheck },
  { type: "Payment Slip / Bank Transfer", icon: Receipt },
];
const DOC_STATUS = {
  pending: "border-amber-400/50 text-amber-300",
  verified: "border-emerald-400/50 text-emerald-300",
  rejected: "border-red-400/50 text-red-300",
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contact, setContact] = useState(null);
  const [docs, setDocs] = useState([]);
  const [payment, setPayment] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [team, setTeam] = useState([]);
  const [uploadType, setUploadType] = useState(DOC_TYPES[0].type);
  const [note, setNote] = useState("");
  const [ledger, setLedger] = useState({ label: "", amount: "" });
  const [saving, setSaving] = useState(false);
  const [calling, setCalling] = useState(false);
  const [billing, setBilling] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const isOwner = user?.role === "owner";

  const generateBill = async () => {
    setBilling(true);
    try {
      const { data } = await api.post("/payments/invoice", { contact_id: id });
      toast.success(`Payment slip generated (${data.invoice_no})`);
      const d = await api.get(`/documents?contact_id=${id}`);
      setDocs(d.data);
      const token = localStorage.getItem("token");
      window.open(`${API}/documents/${data.id}/download?auth=${token}`, "_blank");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBilling(false);
    }
  };

  const callCustomer = async () => {
    setCalling(true);
    try {
      const res = await api.post("/voice/call-customer", { contact_id: id });
      toast.success(
        res.data.simulated
          ? `Outbound call initiated! Customer sees Caller ID: ${res.data.caller_id_shown}`
          : `Connecting via Exotel! Customer sees: ${res.data.caller_id_shown}`
      );
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCalling(false);
    }
  };

  const load = useCallback(async () => {
    const [c, d, p, tl] = await Promise.all([
      api.get(`/contacts/${id}`),
      api.get(`/documents?contact_id=${id}`),
      api.get(`/payments?contact_id=${id}`),
      api.get(`/contacts/${id}/timeline`),
    ]);
    setContact(c.data);
    setDocs(d.data);
    setPayment(p.data);
    setTimeline(tl.data.messages);
    const tk = await api.get(`/tickets?status=all`);
    setTicket(tk.data.find((x) => x.contact_id === id && x.status !== "resolved") || tk.data.find((x) => x.contact_id === id));
    if (user?.role === "owner") api.get(`/team`).then((r) => setTeam(r.data.filter((m) => m.role === "agent")));
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("workspace_id", localStorage.getItem("workspace_id"));
    fd.append("contact_id", id);
    fd.append("doc_type", uploadType);
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      const d = await api.get(`/documents?contact_id=${id}`);
      setDocs(d.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  const setDocStatus = async (did, status) => {
    await api.put(`/documents/${did}/status?status=${status}`);
    const d = await api.get(`/documents?contact_id=${id}`);
    setDocs(d.data);
  };

  const removeDoc = async (did) => {
    await api.delete(`/documents/${did}`);
    setDocs((prev) => prev.filter((x) => x.id !== did));
  };

  const download = (doc) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/documents/${doc.id}/download?auth=${token}`, "_blank");
  };

  const savePayment = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post("/payments", { workspace_id: localStorage.getItem("workspace_id"), contact_id: id, ...payload });
      setPayment(data);
      toast.success("Package saved to customer");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addLedger = async () => {
    if (!payment || !ledger.label || !ledger.amount) return;
    await api.post(`/payments/${payment.id}/ledger`, { label: ledger.label, amount: parseFloat(ledger.amount) });
    setLedger({ label: "", amount: "" });
    const p = await api.get(`/payments?contact_id=${id}`);
    setPayment(p.data);
  };

  const addNote = async () => {
    if (!ticket || !note) return;
    await api.post(`/tickets/${ticket.id}/notes`, { body: note });
    setNote("");
    const tk = await api.get(`/tickets/${ticket.id}`);
    setTicket(tk.data);
  };

  const updateTicket = async (patch) => {
    if (!ticket) return;
    const { data } = await api.put(`/tickets/${ticket.id}`, patch);
    setTicket({ ...ticket, ...data });
    toast.success("Ticket updated");
  };

  const reassign = async (agentId) => {
    await api.put(`/contacts/${id}`, {
      workspace_id: localStorage.getItem("workspace_id"),
      name: contact.name, phone: contact.phone, email: contact.email, tags: contact.tags, agent_id: agentId,
    });
    load();
    toast.success("Associate Partner reassigned");
  };

  const deleteCustomer = async () => {
    setDeletingCustomer(true);
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Customer removed");
      navigate("/customers");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setDeletingCustomer(false);
    }
  };

  if (!contact) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const paid = (payment?.ledger || []).reduce((s, l) => s + l.amount, 0);
  const balance = (payment?.total || 0) - paid;

  return (
    <div>
      <div className="brand-gradient border-b border-border/60 px-4 sm:px-8 py-5">
        <Button variant="ghost" size="sm" onClick={() => navigate("/customers")} className="text-emerald-100/70 gap-1.5 mb-3 -ml-2">
          <ArrowLeft className="w-4 h-4" /> Customers
        </Button>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-primary/60 border border-gold/40 flex items-center justify-center text-2xl font-serif text-gold">{contact.name[0]}</div>
          <div>
            <h1 className="text-2xl font-serif font-semibold text-white">{contact.customer_no && <span className="text-gold font-mono text-base mr-2" data-testid="customer-detail-no">Customer #{contact.customer_no}</span>}{contact.name}</h1>
            <p className="text-emerald-100/60 text-sm font-mono">{contact.phone}{contact.email ? ` · ${contact.email}` : ""}</p>
            <button
              data-testid="copy-customer-id"
              onClick={() => { navigator.clipboard.writeText(contact.id); toast.success("Customer ID copied — matches Excel"); }}
              className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-mono text-gold/80 hover:text-gold bg-gold/10 border border-gold/30 rounded px-2 py-0.5"
              title="Copy full Customer ID (matches Excel export)"
            >
              <Copy className="w-3 h-3" /> ID: {contact.id.slice(0, 8)}…
            </button>
          </div>
          <div className="flex flex-wrap gap-2 ml-auto items-center">
            {isOwner && (
              <Button
                onClick={() => setDeleteOpen(true)}
                data-testid="delete-customer-btn"
                variant="ghost"
                className="text-destructive hover:bg-destructive/15 gap-1.5 text-xs h-9"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Customer
              </Button>
            )}
            <Button
              onClick={callCustomer}
              disabled={calling}
              data-testid="call-customer-btn"
              className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5 text-xs h-9"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              {calling ? "Dialing..." : "Call Customer"}
            </Button>
            {(contact.tags || []).map((t) => <Badge key={t} variant="outline" className="border-gold/40 text-gold">{t}</Badge>)}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-8">
        {isOwner && <div className="mb-5"><AddressCard client={api} path={`/address/customer/${id}`} title="Customer's Full Address" /></div>}
        <Tabs defaultValue="documents">
          <TabsList className="bg-card/40 border border-border/60">
            <TabsTrigger value="documents" data-testid="tab-documents">Document Vault</TabsTrigger>
            <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>
            <TabsTrigger value="ticket" data-testid="tab-ticket">Ticket & Notes</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>

          {/* Documents */}
          <TabsContent value="documents" className="mt-5">
            <Card className="p-4 bg-card/40 border-border/60 mb-4">
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-xs text-muted-foreground">Document type</Label>
                  <Select value={uploadType} onValueChange={setUploadType}>
                    <SelectTrigger className="w-64 mt-1 bg-card/60" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.type} value={d.type}>{d.type}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <label>
                  <input type="file" className="hidden" onChange={upload} data-testid="doc-upload-input" accept=".pdf,.png,.jpg,.jpeg,.webp" />
                  <span data-testid="doc-upload-trigger" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-sm cursor-pointer">
                    <Upload className="w-4 h-4" /> Upload (visa / ticket / NOC / slip)
                  </span>
                </label>
              </div>
            </Card>

            <div className="grid sm:grid-cols-2 gap-4">
              {docs.length === 0 && <p className="text-sm text-muted-foreground col-span-2 py-8 text-center">No documents yet. Upload a payment slip, visa, ticket or NOC.</p>}
              {docs.map((doc) => {
                const meta = DOC_TYPES.find((d) => d.type === doc.type);
                const Icon = meta?.icon || FileCheck;
                return (
                  <Card key={doc.id} data-testid={`doc-card-${doc.id}`} className="p-4 bg-card/40 border-border/60 card-hover">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/40 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-gold" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{doc.type}</p>
                        <p className="text-xs text-muted-foreground truncate">{doc.original_filename}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="outline" className={`text-[10px] ${DOC_STATUS[doc.status]}`}>{doc.status}</Badge>
                          <span className="text-[10px] text-muted-foreground">{timeAgo(doc.created_at)} · {doc.uploaded_by}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button size="sm" variant="outline" data-testid={`doc-download-btn-${doc.id}`} onClick={() => download(doc)} className="gap-1.5 border-gold/40 text-gold flex-1"><Download className="w-3.5 h-3.5" /> Download</Button>
                      <Select value={doc.status} onValueChange={(s) => setDocStatus(doc.id, s)}>
                        <SelectTrigger className="w-28 h-8 bg-card/60 text-xs" data-testid={`doc-status-${doc.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" onClick={() => removeDoc(doc.id)} className="text-destructive h-8 w-8" data-testid={`doc-delete-${doc.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Payment */}
          <TabsContent value="payment" className="mt-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">Build the package, record instalments, then issue a printable slip.</p>
              <Button
                data-testid="generate-bill-btn"
                onClick={generateBill}
                disabled={billing || !payment}
                className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"
              >
                <Receipt className="w-4 h-4" /> {billing ? "Generating…" : "Generate Bill / Slip"}
              </Button>
            </div>
            {payment && (
              <div className="grid md:grid-cols-3 gap-4 mb-5">
                <StatCard label="Package Total" value={inr(payment.total)} sub={payment.package} />
                <StatCard label="Paid" value={inr(paid)} sub={`${payment.ledger?.length || 0} instalments`} />
                <StatCard label="Balance Due" value={inr(balance)} sub={balance <= 0 ? "Fully paid" : "Pending"} highlight={balance > 0} />
              </div>
            )}
            {payment?.plan && <div className="mb-5"><PaymentStages plan={payment.plan} /></div>}
            <Card className="p-5 bg-card/30 border-border/60 mb-5">
              <div className="flex items-center justify-between mb-4">
                <p className="eyebrow text-gold flex items-center gap-2"><CalcIcon className="w-4 h-4" /> Package Calculator</p>
                {isOwner && (
                  <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="detail-edit-pricing-btn" className="border-gold/40 text-gold hover:bg-gold/15 gap-1.5 text-xs h-8">
                        <Settings2 className="w-3.5 h-3.5" /> Edit Packages & Pricing
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#05261D] border-gold/25 max-w-3xl max-h-[85vh] overflow-auto">
                      <DialogHeader>
                        <DialogTitle className="font-serif text-gold flex items-center gap-2"><Settings2 className="w-5 h-5" /> Edit Packages & Pricing</DialogTitle>
                        <DialogDescription>Add, edit or remove packages, room types, add-ons and the visa fee.</DialogDescription>
                      </DialogHeader>
                      <PricingEditor onSaved={() => setPricingOpen(false)} />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <PackageCalculator
                initial={payment ? { package_id: undefined, occupancy_id: undefined, visa_insurance: payment.visa_insurance } : null}
                onSave={savePayment} saving={saving} />
            </Card>

            {payment && (
              <Card className="p-5 bg-card/30 border-border/60">
                <p className="eyebrow text-gold mb-4 flex items-center gap-2"><Wallet className="w-4 h-4" /> Payment Ledger</p>
                <div className="space-y-2 mb-4">
                  {(payment.ledger || []).map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-border/40">
                      <span>{l.label}</span>
                      <span className="font-mono text-gold">{inr(l.amount)}</span>
                    </div>
                  ))}
                  {(!payment.ledger || payment.ledger.length === 0) && <p className="text-xs text-muted-foreground">No payments recorded.</p>}
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1"><Label className="text-xs text-muted-foreground">Label</Label><Input data-testid="ledger-label-input" value={ledger.label} onChange={(e) => setLedger({ ...ledger, label: e.target.value })} placeholder="Token deposit / Milestone" className="mt-1 bg-card/60" /></div>
                  <div className="w-36"><Label className="text-xs text-muted-foreground">Amount ₹</Label><Input data-testid="ledger-amount-input" type="number" value={ledger.amount} onChange={(e) => setLedger({ ...ledger, amount: e.target.value })} className="mt-1 bg-card/60" /></div>
                  <Button data-testid="add-ledger-btn" onClick={addLedger} className="bg-gold text-[#0a2e22] hover:bg-gold/90"><Plus className="w-4 h-4" /></Button>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Ticket */}
          <TabsContent value="ticket" className="mt-5">
            {ticket ? (
              <div className="grid md:grid-cols-3 gap-5">
                <Card className="p-5 bg-card/40 border-border/60 md:col-span-1 space-y-4">
                  <div><Label className="text-xs text-muted-foreground">Subject</Label><p className="text-sm font-medium mt-1">{ticket.subject}</p></div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={ticket.status} onValueChange={(s) => updateTicket({ status: s })}>
                      <SelectTrigger className="mt-1 bg-card/60" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {user?.role === "owner" && team.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Assigned associate partner (owns commission)</Label>
                      <Select value={contact.agent_id || ""} onValueChange={reassign}>
                        <SelectTrigger className="mt-1 bg-card/60" data-testid="assignee-select"><SelectValue placeholder="Assign member" /></SelectTrigger>
                        <SelectContent>{team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </Card>
                <Card className="p-5 bg-card/40 border-border/60 md:col-span-2">
                  <p className="eyebrow text-gold mb-3 flex items-center gap-2"><StickyNote className="w-4 h-4" /> Internal Notes</p>
                  <div className="space-y-2 mb-4 max-h-64 overflow-auto">
                    {(ticket.notes || []).map((n) => (
                      <div key={n.id} className="bg-card/60 rounded-lg p-3 text-sm">
                        <p>{n.body}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{n.author} · {timeAgo(n.created_at)}</p>
                      </div>
                    ))}
                    {(!ticket.notes || ticket.notes.length === 0) && <p className="text-xs text-muted-foreground">No notes yet.</p>}
                  </div>
                  <div className="flex gap-2">
                    <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" rows={2} className="resize-none bg-card/60" />
                    <Button data-testid="add-note-btn" onClick={addNote} disabled={!note} className="bg-gold text-[#0a2e22] hover:bg-gold/90 self-end">Add</Button>
                  </div>
                </Card>
              </div>
            ) : <p className="text-sm text-muted-foreground py-8 text-center">No ticket for this customer yet.</p>}
          </TabsContent>

          {/* History */}
          <TabsContent value="history" className="mt-5">
            <Card className="p-5 bg-card/40 border-border/60">
              <div className="space-y-3">
                {timeline.map((m) => (
                  <div key={m.id} className="flex gap-3 text-sm border-b border-border/40 pb-3">
                    <span className="eyebrow text-[9px] text-gold w-16 flex-shrink-0 pt-1">{m.channel}</span>
                    <div className="flex-1"><p>{m.body}</p><p className="text-[10px] text-muted-foreground mt-0.5">{m.direction} · {fmtTime(m.created_at)}</p></div>
                  </div>
                ))}
                {timeline.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No interaction history.</p>}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Customer Confirmation Modal */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-[#05261D] border-destructive/30" data-testid="delete-customer-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete Customer Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong className="text-white">{contact.name}</strong> ({contact.phone})? This will delete their tickets, payment records, and messages. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} className="text-muted-foreground">Cancel</Button>
            <Button data-testid="confirm-delete-customer-btn" onClick={deleteCustomer} disabled={deletingCustomer} className="bg-destructive text-white hover:bg-destructive/90 font-semibold">
              {deletingCustomer ? "Deleting..." : "Delete Customer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <Card className={`p-4 bg-card/40 border-border/60 ${highlight ? "border-gold/40 gold-glow" : ""}`}>
      <p className="eyebrow text-muted-foreground">{label}</p>
      <p className={`text-2xl font-serif font-semibold mt-1 ${highlight ? "text-gold" : "text-white"}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>
    </Card>
  );
}
